import { getStoredLocale, i18next, type Locale, setStoredLocale, useLocale } from '@multaqa/i18n'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import { currentUserQuery } from './auth'
import { meQuery } from './me'

let appliedLocale: Locale | null = null

/**
 * Apply the account locale when no browser choice exists. Repeated route guards
 * are no-ops, while a choice made on a guest screen is picked up on entry.
 */
export function applyStoredLocaleOnce(next: Locale): void {
  const locale = getStoredLocale() ?? next
  if (appliedLocale === locale) return

  appliedLocale = locale
  setStoredLocale(locale)
  void i18next.changeLanguage(locale)
}

/** Tests only: forget the applied locale, so each test starts as a fresh page load. */
export function resetAppliedLocale(): void {
  appliedLocale = null
}

export function useSetLocale(): {
  setLocale: (next: Locale) => void
  isError: boolean
} {
  const { setLocale: setLocalLocale } = useLocale()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (next: Locale) => {
      const { data, error } = await api.PATCH('/me/profile', { body: { locale: next } })
      if (error) throw new Error(error.message ?? 'Failed to update locale')
      return data
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: currentUserQuery.queryKey }),
        queryClient.invalidateQueries({ queryKey: meQuery.queryKey }),
      ])
    },
  })

  return {
    setLocale: (next) => {
      setLocalLocale(next)
      // Guest screens can change the browser preference, but they have no
      // profile to update. The authenticated route has already resolved
      // `/user` before its shell renders, so a successful query is the safe
      // signal that this write belongs to a signed-in user.
      if (queryClient.getQueryState(currentUserQuery.queryKey)?.status === 'success') {
        mutation.mutate(next)
      }
    },
    isError: mutation.isError,
  }
}
